/**
 * IndexedDB wrapper (spec section 33).
 *
 * Video binaries never go into localStorage - it is a synchronous,
 * ~5 MB string store and would break on the first real clip. IndexedDB
 * is used instead, with an in-memory fallback so the app still runs in
 * private windows where IndexedDB can be unavailable.
 */

import { STORAGE } from '../core/config.js';
import { createLogger } from '../utils/log.js';

const log = createLogger('storage');

let dbPromise = null;
let fallbackReason = null;

const memoryFallback = {
  data: new Map(),
  get(store, key) {
    return this.data.get(`${store}:${key}`) ?? null;
  },
  put(store, value, keyPath) {
    this.data.set(`${store}:${value[keyPath]}`, value);
    return value;
  },
  delete(store, key) {
    return this.data.delete(`${store}:${key}`);
  },
  getAll(store) {
    const prefix = `${store}:`;
    return Array.from(this.data.entries())
      .filter(([k]) => k.startsWith(prefix))
      .map(([, v]) => v);
  },
  clear(store) {
    const prefix = `${store}:`;
    for (const key of Array.from(this.data.keys())) {
      if (key.startsWith(prefix)) this.data.delete(key);
    }
  },
};

export function isUsingFallback() {
  return Boolean(fallbackReason);
}

export function getStorageDiagnostics() {
  return {
    driver: fallbackReason ? 'memory-fallback' : 'indexeddb',
    fallbackReason,
    database: STORAGE.dbName,
    version: STORAGE.dbVersion,
  };
}

function openDatabase() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      fallbackReason = 'IndexedDB is not available in this browser context.';
      resolve(null);
      return;
    }
    let request;
    try {
      request = indexedDB.open(STORAGE.dbName, STORAGE.dbVersion);
    } catch (error) {
      fallbackReason = `IndexedDB could not be opened: ${error.message}`;
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      for (const [name, config] of Object.entries({
        [STORAGE.stores.assets]: { keyPath: 'id' },
        [STORAGE.stores.scenes]: { keyPath: 'id' },
        [STORAGE.stores.prompts]: { keyPath: 'id' },
        [STORAGE.stores.history]: { keyPath: 'id' },
        [STORAGE.stores.settings]: { keyPath: 'key' },
      })) {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, config);
          if (name === STORAGE.stores.history) {
            store.createIndex('createdAt', 'createdAt', { unique: false });
            store.createIndex('sceneId', 'sceneId', { unique: false });
          }
          if (name === STORAGE.stores.assets) {
            store.createIndex('sceneId', 'sceneId', { unique: false });
          }
        }
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => {
      fallbackReason = `IndexedDB open failed: ${request.error?.message ?? 'unknown error'}`;
      log.warn('falling back to in-memory storage', fallbackReason);
      resolve(null);
    };
    request.onblocked = () => {
      fallbackReason = 'IndexedDB upgrade blocked by another open tab.';
      resolve(null);
    };
  });
  return dbPromise;
}

function tx(db, storeName, mode) {
  return db.transaction(storeName, mode).objectStore(storeName);
}

function wrap(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

export const storage = {
  async get(store, key) {
    const db = await openDatabase();
    if (!db) return memoryFallback.get(store, key);
    try {
      return (await wrap(tx(db, store, 'readonly').get(key))) ?? null;
    } catch (error) {
      log.error('get failed', error);
      return null;
    }
  },

  async put(store, value) {
    const db = await openDatabase();
    if (!db) return memoryFallback.put(store, value, 'id');
    try {
      await wrap(tx(db, store, 'readwrite').put(value));
      return value;
    } catch (error) {
      log.error('put failed', error);
      throw error;
    }
  },

  async delete(store, key) {
    const db = await openDatabase();
    if (!db) return memoryFallback.delete(store, key);
    try {
      await wrap(tx(db, store, 'readwrite').delete(key));
      return true;
    } catch (error) {
      log.error('delete failed', error);
      return false;
    }
  },

  async getAll(store) {
    const db = await openDatabase();
    if (!db) return memoryFallback.getAll(store);
    try {
      return (await wrap(tx(db, store, 'readonly').getAll())) ?? [];
    } catch (error) {
      log.error('getAll failed', error);
      return [];
    }
  },

  async clear(store) {
    const db = await openDatabase();
    if (!db) {
      memoryFallback.clear(store);
      return true;
    }
    try {
      await wrap(tx(db, store, 'readwrite').clear());
      return true;
    } catch (error) {
      log.error('clear failed', error);
      return false;
    }
  },

  /**
   * Best-effort storage estimate. `navigator.storage.estimate()` is
   * available in Chrome/Edge/Firefox and returns 0s elsewhere.
   */
  async estimate() {
    if (!navigator.storage?.estimate) {
      return { supported: false, usageBytes: 0, quotaBytes: 0, usagePercent: 0, persisted: false };
    }
    try {
      const { usage = 0, quota = 0 } = await navigator.storage.estimate();
      const persisted = navigator.storage.persisted ? await navigator.storage.persisted() : false;
      return {
        supported: true,
        usageBytes: usage,
        quotaBytes: quota,
        usagePercent: quota ? (usage / quota) * 100 : 0,
        persisted,
      };
    } catch {
      return { supported: false, usageBytes: 0, quotaBytes: 0, usagePercent: 0, persisted: false };
    }
  },

  /** Ask the browser to keep our data (used from Settings). */
  async requestPersistence() {
    if (!navigator.storage?.persist) return false;
    try {
      return await navigator.storage.persist();
    } catch {
      return false;
    }
  },
};
